import { cn } from '@gymcoach/ui';
import { Link, useMatches } from 'react-router-dom';

/**
 * Breadcrumbs, driven by route handles rather than by parsing the URL (ported from
 * legalcrm). A route declares its own crumb, so a client shows their name, not a UUID:
 *
 *   { path: ':clientId', handle: { crumb: 'Client' } }
 */
export interface RouteHandle {
  crumb?: string | (() => string);
}

export function Breadcrumbs({ className }: { className?: string }) {
  const matches = useMatches();

  const crumbs = matches
    .map((match) => {
      const handle = match.handle as RouteHandle | undefined;
      if (!handle?.crumb) return null;
      const label = typeof handle.crumb === 'function' ? handle.crumb() : handle.crumb;
      return { label, to: match.pathname };
    })
    .filter((crumb): crumb is { label: string; to: string } => crumb !== null)
    // Two routes on one path (an area and its index page) would repeat the crumb.
    .filter((crumb, index, all) => index === 0 || all[index - 1]!.to !== crumb.to);

  if (crumbs.length === 0) return <div className={className} />;

  return (
    <nav aria-label="Breadcrumb" className={cn('items-center gap-1.5 text-sm', className)}>
      <ol className="flex items-center gap-1.5">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li key={crumb.to} className="flex items-center gap-1.5">
              {index > 0 && (
                <span className="text-ink-subtle" aria-hidden="true">
                  /
                </span>
              )}
              {last ? (
                <span className="truncate font-medium text-ink" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.to} className="truncate text-ink-muted hover:text-ink">
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
